import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout/Layout';
import { ErrorBoundary } from './components/UI/ErrorBoundary';
import { PageLoader } from './components/UI/PageLoader';
import { Dialog } from './components/UI/Dialog';
import { HostSalonDialog } from './components/UI/HostSalonDialog';
import { PaymentDialog } from './components/UI/PaymentDialog';
import { CorporateTrainingDialog } from './components/UI/CorporateTrainingDialog';
import './index.css';
// Route prefetch helpers — call these on hover/intent to preload chunks
export const prefetchRoutes = {
  class: () => import('./pages/AIClass'),
  lounge: () => import('./pages/LoungeOnboard'),
  training: () => import('./pages/Training'),
  events: () => import('./pages/Events')
};
// Lazy-loaded pages — only loaded when the route is visited
const Home = lazy(() => import('./pages/Home').then(m => ({
  default: m.Home
})));
const Gallery = lazy(() => import('./pages/Gallery').then(m => ({
  default: m.Gallery
})));
const About = lazy(() => import('./pages/About').then(m => ({
  default: m.About
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Toolkits = lazy(() => import('./pages/Toolkits').then(m => ({
  default: m.Toolkits
})));
const NetworkingGuide = lazy(() => import('./pages/NetworkingGuide').then(m => ({
  default: m.NetworkingGuide
})));
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({
  default: m.FAQ
})));
const Partners = lazy(() => import('./pages/Partners').then(m => ({
  default: m.Partners
})));
const Events = lazy(() => import('./pages/Events').then(m => ({
  default: m.Events
})));
const SuccessStories = lazy(() => import('./pages/SuccessStories').then(m => ({
  default: m.SuccessStories
})));
const PartnerWithUs = lazy(() => import('./pages/PartnerWithUs').then(m => ({
  default: m.PartnerWithUs
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(m => ({
  default: m.CookiePolicy
})));
const Portfolio = lazy(() => import('./pages/Portfolio').then(m => ({
  default: m.Portfolio
})));
const Membership = lazy(() => import('./pages/Membership').then(m => ({
  default: m.Membership
})));
const JoinTeam = lazy(() => import('./pages/JoinTeam').then(m => ({
  default: m.JoinTeam
})));
const JoinNow = lazy(() => import('./pages/JoinNow').then(m => ({
  default: m.JoinNow
})));
const InnerCircle = lazy(() => import('./pages/InnerCircle').then(m => ({
  default: m.InnerCircle
})));
const LifetimePartner = lazy(() => import('./pages/LifetimePartner').then(m => ({
  default: m.LifetimePartner
})));
const WaitingRoom = lazy(() => import('./pages/WaitingRoom').then(m => ({
  default: m.WaitingRoom
})));
const WarriEvent2025 = lazy(() => import('./pages/WarriEvent2025').then(m => ({
  default: m.WarriEvent2025
})));
const HowToEngage = lazy(() => import('./pages/HowToEngage').then(m => ({
  default: m.HowToEngage
})));
const HostSalon = lazy(() => import('./pages/HostSalon').then(m => ({
  default: m.HostSalon
})));
const Spotlight = lazy(() => import('./pages/Spotlight').then(m => ({
  default: m.Spotlight
})));
const BibiEnejeSpotlight = lazy(() => import('./pages/BibiEnejeSpotlight').then(m => ({
  default: m.BibiEnejeSpotlight
})));
const AugustineSpotlight = lazy(() => import('./pages/AugustineSpotlight').then(m => ({
  default: m.AugustineSpotlight
})));
const DrOsagieSpotlight = lazy(() => import('./pages/DrOsagieSpotlight').then(m => ({
  default: m.DrOsagieSpotlight
})));
const SalonSpotlight = lazy(() => import('./pages/SalonSpotlight').then(m => ({
  default: m.SalonSpotlight
})));
const Team = lazy(() => import('./pages/Team').then(m => ({
  default: m.Team
})));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail').then(m => ({
  default: m.TeamMemberDetail
})));
const AIPowerBundle = lazy(() => import('./pages/AIPowerBundle').then(m => ({
  default: m.AIPowerBundle
})));
const AIMasterclass = lazy(() => import('./pages/AIMasterclass').then(m => ({
  default: m.AIMasterclass
})));
const AIFormTest = lazy(() => import('./pages/AIFormTest').then(m => ({
  default: m.AIFormTest
})));
const AIKids = lazy(() => import('./pages/AIKids').then(m => ({
  default: m.AIKids
})));
const KidsAIProgram = lazy(() => import('./pages/KidsAIProgram').then(m => ({
  default: m.KidsAIProgram
})));
const Training = lazy(() => import('./pages/Training').then(m => ({
  default: m.Training
})));
const LoungeOnboard = lazy(() => import('./pages/LoungeOnboard').then(m => ({
  default: m.LoungeOnboard
})));
const AIClass = lazy(() => import('./pages/AIClass').then(m => ({
  default: m.AIClass
})));
const JoinTNS = lazy(() => import('./pages/JoinTNS').then(m => ({
  default: m.JoinTNS
})));
const GreenEvent = lazy(() => import('./pages/GreenEvent').then(m => ({
  default: m.GreenEvent
})));
const GreenSpeakerDetail = lazy(() => import('./pages/GreenSpeakerDetail').then(m => ({
  default: m.GreenSpeakerDetail
})));
// ScrollToTop component that scrolls to top on route change
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isHostFormOpen, setIsHostFormOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isCorporateTrainingOpen, setIsCorporateTrainingOpen] = useState(false);
  // Preload Paystack script so it's cached before user reaches /class or payment pages
  useEffect(() => {
    // Only preload if not already present
    if (!document.querySelector('link[href*="paystack"]')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = 'https://js.paystack.co/v1/inline.js';
      link.as = 'script';
      document.head.appendChild(link);
    }
  }, []);
  const openRegistrationForm = () => {
    setIsFormOpen(true);
  };
  const openHostSalonForm = () => {
    setIsHostFormOpen(true);
  };
  const openPaymentForm = () => {
    setIsPaymentOpen(true);
  };
  const openCorporateTrainingForm = () => {
    setIsCorporateTrainingOpen(true);
  };
  useEffect(() => {
    window.openRegistrationForm = openRegistrationForm;
    window.openHostSalonForm = openHostSalonForm;
    window.openPaymentForm = openPaymentForm;
    window.openCorporateTrainingForm = openCorporateTrainingForm;
    return () => {
      delete window.openRegistrationForm;
      delete window.openHostSalonForm;
      delete window.openPaymentForm;
      delete window.openCorporateTrainingForm;
    };
  }, []);
  return <Router data-id="element-0" data-mp="2pez96o8jjxql">
      <ScrollToTop data-id="element-1" data-mp="2pfhaw0dz345w" />
      <ErrorBoundary data-id="element-2" data-mp="2pg0gqiqcm23k">
        <Suspense fallback={<PageLoader data-id="element-4" data-mp="2pcuvjekubkd4" />} data-id="element-3" data-mp="2pgike0fcbili">
          <Routes data-id="element-5" data-mp="2udskca0cvcnl">
            <Route path="/lounge" element={<LoungeOnboard data-id="element-7" data-mp="2uek32k8utfwg" />} data-id="element-6" data-mp="2ud94qzy6h0cq" />
            <Route path="/class" element={<AIClass data-id="element-9" data-mp="2ge0zpqfc2y0m" />} data-id="element-8" data-mp="2ge9e5mr3ubdx" />
            <Route path="/join-tns" element={<JoinTNS data-id="element-11" data-mp="2ggn7nr64zfjd" />} data-id="element-10" data-mp="2gfkjvs441t7o" />
            <Route path="/*" element={<Layout openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} openPaymentForm={openPaymentForm} data-id="element-13" data-mp="2khp8udjm61nz">
                  <Suspense fallback={<PageLoader data-id="element-15" data-mp="2qwoar55lumzy" />} data-id="element-14" data-mp="2qx7hdppt1oyo">
                    <Routes data-id="element-16" data-mp="2qvmnm2bbea1v">
                      <Route path="/" element={<Home openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} openPaymentForm={openPaymentForm} data-id="element-18" data-mp="2m0h397xk93fw" />} data-id="element-17" data-mp="2qz0rjezmmrbf" />
                      <Route path="/green" element={<GreenEvent data-id="element-20" data-mp="238ej3mlpn50w" />} data-id="element-19" data-mp="237mzla5e0xqz" />
                      <Route path="/green/speaker/:id" element={<GreenSpeakerDetail data-id="element-22" data-mp="1y80mr1th1jip" />} data-id="element-21" data-mp="239g8kw3h3u25" />
                      <Route path="/gallery" element={<Gallery data-id="element-24" data-mp="1u7gofux5w9uz" />} data-id="element-23" data-mp="1u6orogrtw634" />
                      <Route path="/about" element={<About data-id="element-26" data-mp="9po0cvmlerzm" />} data-id="element-25" data-mp="9owguj69skpp" />
                      <Route path="/team" element={<Team data-id="element-28" data-mp="9r8y0u47er1p" />} data-id="element-27" data-mp="9od035scw2d9" />
                      <Route path="/team/:id" element={<TeamMemberDetail data-id="element-30" data-mp="dowtpzdq0nun" />} data-id="element-29" data-mp="dpq4rcjppzil" />
                      <Route path="/blog" element={<Blog data-id="element-32" data-mp="iqm5jh8rp5js" />} data-id="element-31" data-mp="ipu9y6f677ti" />
                      <Route path="/blog/:slug" element={<BlogPost data-id="element-34" data-mp="nofbqaa2lzk2" />} data-id="element-33" data-mp="iqdpxhl9fm4w" />
                      <Route path="/contact" element={<Contact data-id="element-36" data-mp="nmkzrlgs1hs4" />} data-id="element-35" data-mp="nnclm4kko157" />
                      <Route path="/toolkits" element={<Toolkits data-id="element-38" data-mp="3m7m6ly494fy1" />} data-id="element-37" data-mp="3m6tadr2x58u5" />
                      <Route path="/networking-guide" element={<NetworkingGuide data-id="element-40" data-mp="3r3bc0i2ycr29" />} data-id="element-39" data-mp="3m4gh4hrpnc1s" />
                      <Route path="/faq" element={<FAQ openRegistrationForm={openRegistrationForm} data-id="element-42" data-mp="4ql708fz6a7h" />} data-id="element-41" data-mp="3w3p8r3z3sekz" />
                      <Route path="/partners" element={<Partners data-id="element-44" data-mp="38emu84zrn1up" />} data-id="element-43" data-mp="33fjqcnviv1ob" />
                      <Route path="/events" element={<Events data-id="element-46" data-mp="38ctkuhxrq3j0" />} data-id="element-45" data-mp="38dlhzx70k9be" />
                      <Route path="/events/warri-2025" element={<WarriEvent2025 data-id="element-48" data-mp="2k685pv5a319z" />} data-id="element-47" data-mp="2f7dj0i46wskl" />
                      <Route path="/success-stories" element={<SuccessStories data-id="element-50" data-mp="2p5ugzz1jq7g5" />} data-id="element-49" data-mp="2k4di292mgv27" />
                      <Route path="/portfolio" element={<Portfolio data-id="element-52" data-mp="2u3vzoiv284r3" />} data-id="element-51" data-mp="2u4nuvskqw0gu" />
                      <Route path="/membership" element={<Membership data-id="element-54" data-mp="1whf5hmhmi79k" />} data-id="element-53" data-mp="1wgnk14hqppx0" />
                      <Route path="/join-team" element={<JoinTeam data-id="element-56" data-mp="21fgvyshdp8v2" />} data-id="element-55" data-mp="1wej4fpahb2gw" />
                      <Route path="/joinnow" element={<JoinNow data-id="element-58" data-mp="21dmc7hhufmsk" />} data-id="element-57" data-mp="21gi8z2hyg5ff" />
                      <Route path="/inner-circle" element={<InnerCircle data-id="element-60" data-mp="26d8o9nlxqwzs" />} data-id="element-59" data-mp="26ea19xmihtk5" />
                      <Route path="/lifetime-partner" element={<LifetimePartner data-id="element-62" data-mp="2b7cwsbaund2z" />} data-id="element-61" data-mp="2b5jn0n4xwcqr" />
                      <Route path="/waiting-room" element={<WaitingRoom data-id="element-64" data-mp="1dobg4dbfugkr" />} data-id="element-63" data-mp="1dpcqsgojl11y" />
                      <Route path="/join-the-salon" element={<HowToEngage data-id="element-66" data-mp="1inefbh4qlskt" />} data-id="element-65" data-mp="1iofrjoxhol44" />
                      <Route path="/host-salon" element={<HostSalon data-id="element-68" data-mp="3vy7fs3xxqvyc" />} data-id="element-67" data-mp="3vz8tkg6c5wjr" />
                      <Route path="/spotlight" element={<Spotlight data-id="element-70" data-mp="3rxdn6x9jssz4" />} data-id="element-69" data-mp="3vx60todstpbc" />
                      <Route path="/salon-spotlight" element={<SalonSpotlight data-id="element-72" data-mp="3mxix2vxlk7f4" />} data-id="element-71" data-mp="3ryf59lozefqc" />
                      <Route path="/spotlight/bibi-eneje" element={<BibiEnejeSpotlight data-id="element-74" data-mp="3d0ecv1nbam0y" />} data-id="element-73" data-mp="3i0910xfpcxib" />
                      <Route path="/spotlight/augustine-aigbokhaevbo" element={<AugustineSpotlight data-id="element-76" data-mp="3883brvgu4u6t" />} data-id="element-75" data-mp="3d2r2m0zba0ok" />
                      <Route path="/spotlight/dr-osagie-alex" element={<DrOsagieSpotlight data-id="element-78" data-mp="2zaszq7w7110u" />} data-id="element-77" data-mp="348uguna26q9o" />
                      <Route path="/partner-with-us" element={<PartnerWithUs openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} data-id="element-80" data-mp="2pc3huf4argkl" />} data-id="element-79" data-mp="2uaepqkb17h06" />
                      <Route path="/privacy-policy" element={<PrivacyPolicy data-id="element-82" data-mp="26ouopjookhwt" />} data-id="element-81" data-mp="26mrltz2fiuao" />
                      <Route path="/terms-of-service" element={<TermsOfService data-id="element-84" data-mp="2yez1oxa422o0" />} data-id="element-83" data-mp="2ap4qek5fwm5v" />
                      <Route path="/cookie-policy" element={<CookiePolicy data-id="element-86" data-mp="33bz9bcmlzdh8" />} data-id="element-85" data-mp="33d0tq7pilcbm" />
                      <Route path="/ai-power-bundle" element={<AIPowerBundle data-id="element-88" data-mp="2pldqtp12h5jv" />} data-id="element-87" data-mp="33ev790ygi25o" />
                      <Route path="/ai-masterclass" element={<AIMasterclass data-id="element-90" data-mp="3hcjs48six9aq" />} data-id="element-89" data-mp="2tm5zag2476f3" />
                      <Route path="/ai-form-test" element={<AIFormTest data-id="element-92" data-mp="3m3s0r2m14qmu" />} data-id="element-91" data-mp="3haywefuh3kba" />
                      <Route path="/ai-kids" element={<AIKids data-id="element-94" data-mp="1aojfajnv8ory" />} data-id="element-93" data-mp="3m4tg3ja2vzad" />
                      <Route path="/kids-ai-program" element={<KidsAIProgram data-id="element-96" data-mp="15q87eeh4socd" />} data-id="element-95" data-mp="1anhtpl984jvz" />
                      <Route path="/training" element={<Training data-id="element-98" data-mp="1kem74gj93wop" />} data-id="element-97" data-mp="15nlwq5yy1spx" />
                    </Routes>
                  </Suspense>
                </Layout>} data-id="element-12" data-mp="2kge52dq64twv" />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      <Dialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Join the Salon" size="xl" data-id="element-99" data-mp="rovs859soqrj">
        <div className="w-full" data-id="element-100" data-mp="msouv0vgsnl7">
          <iframe src="https://docs.google.com/forms/d/e/1FAIpQLSdr_5jvYO4hJncEC0hjHQweFJSh8FFpzrBhsjQvCQemuZT7MA/viewform?embedded=true" width="100%" height="600" frameBorder="0" marginHeight={0} marginWidth={0} data-id="element-101" data-mp="11nlcg4p5ynay">
            Loading…
          </iframe>
        </div>
      </Dialog>
      <HostSalonDialog isOpen={isHostFormOpen} onClose={() => setIsHostFormOpen(false)} data-id="element-102" data-mp="2861dqli2905r" />
      <PaymentDialog isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} data-id="element-103" data-mp="hp67mlxc4z3h" />
      <CorporateTrainingDialog isOpen={isCorporateTrainingOpen} onClose={() => setIsCorporateTrainingOpen(false)} data-id="element-104" data-mp="qnhss8ffn4oj" />
    </Router>;
}